import React from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { trafficQualityService } from '../../services/trafficQuality';
import { Card } from '../ui/Card';

function scoreColor(score: number): string {
  if (score >= 80) return 'text-green-600';
  if (score >= 50) return 'text-yellow-600';
  return 'text-red-600';
}

/**
 * Account-wide Traffic Quality summary tile for the dashboard.
 */
export default function TrafficQualityTile() {
  const { t } = useTranslation('trafficQuality');
  const { data, isLoading, isError } = useQuery({
    queryKey: ['tq-account-summary'],
    queryFn: () => trafficQualityService.getAccountSummary('last_7_days'),
    staleTime: 60_000,
  });

  if (isLoading || isError || !data) {
    return null; // stay quiet on the dashboard if unavailable / not entitled
  }

  const s = data.summary;

  return (
    <Card header={<h2 className="font-semibold text-gray-900">{t('tile.title')}</h2>}>
      <div className="grid grid-cols-3 gap-4">
        <div>
          <div className="text-xs text-gray-500">{t('tile.quality_score')}</div>
          <div className={`text-3xl font-bold ${scoreColor(s.quality_score)}`}>
            {s.quality_score}
            <span className="text-sm text-gray-400">/100</span>
          </div>
        </div>
        <div>
          <div className="text-xs text-gray-500">{t('tile.bot_clicks')}</div>
          <div className="text-3xl font-bold text-red-600">{s.bot.toLocaleString()}</div>
          <div className="text-xs text-gray-400">{s.bot_percentage}%</div>
        </div>
        <div>
          <div className="text-xs text-gray-500">{t('tile.open_alerts')}</div>
          <div className="text-3xl font-bold text-orange-600">{s.open_alerts}</div>
        </div>
      </div>

      {data.worst_campaign && data.worst_campaign.bot > 0 && (
        <div className="mt-4 flex items-center justify-between text-sm border-t border-gray-100 pt-3">
          <span className="text-gray-600">
            {t('tile.most_affected')}{' '}
            <Link to={`/campaigns/${data.worst_campaign.campaign_id}/traffic-quality`} className="text-primary-600 hover:underline">
              {data.worst_campaign.name}
            </Link>{' '}
            <span className="text-gray-400">{t('tile.most_affected_pct', { percent: data.worst_campaign.bot_percentage })}</span>
          </span>
        </div>
      )}
    </Card>
  );
}
